import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Document, Page } from 'react-pdf';
import { Orientation } from '@react-pdf/types';
import { saveAs } from 'file-saver';
import { useLanguage } from 'src/hooks';
import {
  Button,
  GroupFilter,
  Modal,
  SelectWithLabel,
} from 'src/components/general';
import styles from './pdfStyle.module.scss';

type PdfModalProps = {
  show: boolean;
  close: () => void;
  pdfBlob: Blob | null;
  fileName: string;
  isLoading: boolean;
  onPdfHandle: (orientation: Orientation) => void;
  pageBreakItems?: { id: string; label: string }[];
  pageBreakParams?: Record<string, boolean | null>;
  setPageBreakParams?: Dispatch<SetStateAction<Record<string, boolean | null>>>;
};

export const PdfModal = ({
  show,
  close,
  pdfBlob,
  fileName,
  isLoading,
  onPdfHandle,
  pageBreakItems,
  pageBreakParams,
  setPageBreakParams,
}: PdfModalProps) => {
  const { t } = useLanguage();

  const [orientation, setOrientation] = useState<Orientation>('portrait');
  const [pdfString, setPdfString] = useState('');
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageBreakSet, setPageBreakSet] = useState<Set<string>>(new Set());
  const [pageWidth, setPageWidth] = useState(300);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!pageBreakParams) {
      return;
    }

    const newSet: Set<string> = new Set();
    Object.entries(pageBreakParams).forEach(([key, value]) => {
      if (value) {
        newSet.add(key);
      }
    });

    setPageBreakSet(newSet);
  }, [pageBreakParams]);

  const onPageBreakExpanded = useCallback(
    (value: boolean) => {
      if (value || !pageBreakSet) {
        return;
      }

      setPageBreakParams?.((prevState) => {
        const hasChanges = Object.entries(prevState).some(
          ([id, value]) => pageBreakSet.has(id) !== Boolean(value),
        );

        if (hasChanges) {
          return Object.entries(prevState).reduce((res, [id]) => {
            return { ...res, [id]: pageBreakSet.has(id) };
          }, {});
        }

        return prevState;
      });
    },
    [pageBreakSet, setPageBreakParams],
  );

  useEffect(() => {
    if (!pdfBlob) {
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(pdfBlob);
    reader.onloadend = () => {
      const base64String = reader.result as string;
      if (base64String) {
        setPdfString(base64String.slice(base64String.indexOf(',') + 1));
      }
    };
  }, [pdfBlob]);

  const updatePageWidth = useCallback(() => {
    if (!show) {
      return;
    }

    const rect = ref?.current?.getBoundingClientRect();
    if (rect) {
      setPageWidth(rect.width - 24);
    }
  }, [show]);

  useEffect(() => {
    updatePageWidth();
  }, [updatePageWidth]);

  useEffect(() => {
    if (!ref.current) {
      return () => undefined;
    }

    let timerId: ReturnType<typeof setTimeout>;

    const observer = new ResizeObserver(() => {
      clearTimeout(timerId);

      timerId = setTimeout(() => {
        updatePageWidth();
      }, 300);
    });
    observer.observe(ref.current);

    return () => {
      clearTimeout(timerId);
      observer.disconnect();
    };
  }, [updatePageWidth]);

  useEffect(
    () => () => {
      if (show) {
        setOrientation('portrait');
      }
    },
    [show],
  );

  return (
    <Modal
      show={show}
      close={close}
      header={t('component.pdf.export')}
      className={styles.modal}
      footer={
        <div className={styles.bottomButtons}>
          <Button
            label={t('general.cancel')}
            onClick={close}
            design="outlineBlue"
            size="small"
          />
          <Button
            label={t('general.save')}
            onClick={() => {
              if (pdfBlob) {
                saveAs(pdfBlob, fileName);
              }
            }}
            design="lightBlue"
            size="small"
            disabled={isLoading}
          />
        </div>
      }
    >
      <div className={styles.modalContent}>
        <div className={styles.topButtons}>
          <SelectWithLabel
            label={t('component.pdf.orientation')}
            value={orientation}
            items={[
              {
                value: 'portrait',
                label: t('component.pdf.orientation.portrait'),
              },
              {
                value: 'landscape',
                label: t('component.pdf.orientation.landscape'),
              },
            ]}
            selectItem={(value) => {
              const selected = value as Orientation;
              if (orientation === selected) {
                return;
              }

              setOrientation(selected);
              onPdfHandle(selected);
            }}
            disabled={isLoading}
            design="filterModal"
            size="small"
            className={styles.orientationSelect}
          />
          {pageBreakItems && (
            <GroupFilter
              label={t('component.pdf.pageBreak')}
              selectedIDs={pageBreakSet}
              items={pageBreakItems}
              selectItem={(id, value) => {
                setPageBreakSet((prevState) => {
                  const set = new Set(prevState);
                  if (value) {
                    set.add(id);
                  } else {
                    set.delete(id);
                  }
                  return set;
                });
              }}
              onExpanded={onPageBreakExpanded}
              hideCount
              size="small"
              className={styles.pageWrapSelect}
              disabled={isLoading}
            />
          )}
        </div>
        <div className={styles.pdfContainer} ref={ref}>
          {pdfString && (
            <Document
              file={`data:application/pdf;base64,${pdfString}`}
              onLoadSuccess={({ numPages }) => {
                setNumPages(numPages);
              }}
            >
              {Array.from(new Array(numPages), (_el, index) => (
                <Page
                  key={`pdf_page_${index + 1}`}
                  pageNumber={index + 1}
                  width={pageWidth}
                  className={styles.pdfPage}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              ))}
            </Document>
          )}
        </div>
      </div>
    </Modal>
  );
};
